import React, { useState, useEffect } from "react";
import { getVideoes, deleteVideo } from "../../../../redux/api/_hr.api";
import { Play, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import { FallingLines } from "react-loader-spinner";

interface UploadedVideo {
  id: string;
  heading: string;
  subheading: string;
  name: string;
  post: string;
  video_key: string;
  thumbnail_key: string;
}

interface DeleteVideoProps {
  refreshTrigger?: number; // Add optional prop to trigger refresh
}

const CDN_URL = "https://d3ir3etrl1n0sj.cloudfront.net/";

function DeleteVideo({ refreshTrigger }: DeleteVideoProps) {
  const [uploadedVideos, setUploadedVideos] = useState<UploadedVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState(false);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const res: UploadedVideo[] = await getVideoes(1, 1000);
      console.log(res);
      const reversed = [...res].reverse();
      setUploadedVideos(reversed);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching videos:", err);
      setError("Failed to load videos");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  // Add useEffect to refetch when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      fetchVideos();
    }
  }, [refreshTrigger]);

  const handleDeleteVideo = async(id: string) => {
    try {
        setStatus(true);
        await deleteVideo(id)
        toast.success("video deleted successfully");
        setStatus(false)
        fetchVideos();
    } catch (error) {
        console.log("error", error);
        toast.error("deletion failed");
        setStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center text-gray-600">Loading videos...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="text-center text-red-600">{error}</div>
      </div>
    );
  }

  if (uploadedVideos.length === 0) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Uploaded Videos</h1>
        <div className="text-center text-gray-600">No videos found</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">Uploaded Videos</h1>
      
      <div className="mb-4 text-sm text-gray-600">
        Total Videos: {uploadedVideos.length}
      </div>

      {/* Videos Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {uploadedVideos.map((video) => (
          <div
            key={video.id}
            className="relative group overflow-hidden rounded-xl border border-gray-200 shadow hover:shadow-xl transition-all duration-300"
          >
            {/* Thumbnail with play button overlay */}
            <div className="relative">
              <img
                src={CDN_URL + video.thumbnail_key}
                alt={video.heading}
                className="w-full h-48 object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <div className="bg-white/80 rounded-full p-2">
                  <Play className="text-blue-600" size={20} />
                </div>
              </div>
            </div>

            {/* Video Details */}
            <div className="p-4 bg-white">
              <h3 className="font-semibold text-sm text-gray-800 mb-2 line-clamp-2">
                {video.heading}
              </h3>
              <p className="text-xs text-gray-600 mb-1">{video.subheading}</p>
              <p className="text-xs text-gray-500">
                By {video.name}<br />
                {video.post}
              </p>
            </div>

            {/* Delete button overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-300 flex items-center justify-center">
              <button
                onClick={() => handleDeleteVideo(video.id)}
                className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-red-600 hover:bg-red-700 text-white p-3 rounded-full shadow-lg transform hover:scale-110"
                aria-label="Delete video"
              >
                {status ? <FallingLines width="25" color="white"/> :
                <Trash2 size={20} />}
              </button>
            </div>

            {/* Video key at bottom */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <p className="text-white text-xs truncate">{video.video_key}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default DeleteVideo;